/**
 * Experiment runner — token-based A/B testing pattern.
 *
 * Adobe pushes the resolved variant(s) into `window.expFlags` themselves (from a Launch rule
 * they own and control) and then dispatch `adobe-target:decisions-ready` on `document` to tell
 * us it's safe to reveal. We never call Alloy directly — this used to do its own
 * `alloy("sendEvent", ...)` call, but the Target team asked us to remove that so they keep full
 * control over what's sent to Adobe. Confirmed contract with them:
 *
 *   window.expFlags = window.expFlags || {};
 *   window.expFlags.platinumprice = "b"; // or "control", one line per experiment key
 *   document.dispatchEvent(new CustomEvent('adobe-target:decisions-ready'));
 *
 * The rest of the architecture (theme owns all markup/CSS, Target only returns a JSON token,
 * pre-built hidden states revealed by a single attribute toggle) is unchanged.
 */
(function () {
  'use strict';

  var READY_EVENT = 'adobe-target:decisions-ready';
  var FAILSAFE_WAIT_MS = 3000;
  var PRE_HIDE_STYLE_ID = 'at-body-style';

  window.expFlags = window.expFlags || {};

  var alreadyAssigned = {};

  function removePreHideStyle() {
    var el = document.getElementById(PRE_HIDE_STYLE_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /**
   * Reveals the correct state for every experiment on the page. Elements are grouped by
   * their `data-exp` key rather than assumed to be nested under one shared root — this
   * lets two independent sections (e.g. the old announcement bar and the new one) each
   * carry `data-exp`/`data-exp-variant` directly, without needing to be merged into a
   * single component. Safe to call more than once (e.g. if Adobe dispatches the ready
   * event progressively, once per experiment) — already-assigned keys aren't re-tracked.
   */
  function applyAll() {
    var groups = {};

    document.querySelectorAll('[data-exp-variant]').forEach(function (el) {
      var key = el.getAttribute('data-exp');
      if (!key) return;
      groups[key] = groups[key] || [];
      groups[key].push(el);
    });

    Object.keys(groups).forEach(function (key) {
      var elements = groups[key];
      var fallback = elements[0].getAttribute('data-exp-default') || 'control';
      var chosen = window.expFlags[key] || fallback;
      var matched = false;

      elements.forEach(function (el) {
        var isMatch = el.getAttribute('data-exp-variant') === chosen;
        el.hidden = !isMatch;
        if (isMatch) matched = true;
      });

      // Safety net: if the chosen variant doesn't exist among the shipped states,
      // force the fallback so something always renders.
      if (!matched) {
        chosen = fallback;
        elements.forEach(function (el) {
          el.hidden = el.getAttribute('data-exp-variant') !== fallback;
        });
      }

      if (!alreadyAssigned[key] && window.dataLayer) {
        alreadyAssigned[key] = true;
        window.dataLayer.push({ event: 'exp_assigned', exp_key: key, exp_variant: chosen });
      }
    });

    removePreHideStyle();
  }

  /**
   * QA/preview override: ?exp_<key>=<variant> in the URL forces that variant, bypassing
   * Adobe entirely. Useful for reviewing each state before a real activity exists.
   * Example: ?exp_announcementbar=b
   */
  function applyUrlOverrides() {
    var params = new URLSearchParams(window.location.search);
    var found = false;

    params.forEach(function (value, name) {
      if (name.indexOf('exp_') !== 0) return;
      window.expFlags[name.slice('exp_'.length)] = value;
      found = true;
    });

    return found;
  }

  function resolve() {
    if (applyUrlOverrides()) {
      applyAll();
      return;
    }

    // Failsafe: if Adobe never dispatches the ready event (blocked, slow, activity not
    // configured, etc.), reveal defaults anyway rather than leaving content hidden forever.
    var failsafeTimer = setTimeout(applyAll, FAILSAFE_WAIT_MS);

  document.addEventListener(READY_EVENT, function () {  
    clearTimeout(failsafeTimer);
    applyAll();
  });
  }

  if (document.readyState !== 'loading') resolve();
  else document.addEventListener('DOMContentLoaded', resolve);
})();
